import React, { useState } from 'react';
import { Box, Chip, CircularProgress, Divider, Paper, Stack, Typography } from '@mui/material';
import type { Box as BoxInfo, Service } from '../../config/infra';
import {
    type BoxStatus, resolveContainerState, extraContainers,
} from '../../api/statusApi';
import ServiceChip from './ServiceChip';
import ServiceDetailDialog from './ServiceDetailDialog';

interface Props {
    box: BoxInfo;
    /** undefined = still loading first result */
    status: BoxStatus | undefined;
}

function reachabilityChip(status: BoxStatus | undefined) {
    if (status === undefined) {
        return <Chip size='small' icon={<CircularProgress size={12} />} label='checking…' variant='outlined' />;
    }
    if (!status.reachable) {
        return <Chip size='small' color='error' label={`unreachable${status.error ? ` — ${status.error}` : ''}`} />;
    }
    const when = status.reportedAt ? new Date(status.reportedAt).toLocaleTimeString() : undefined;
    return <Chip size='small' color='success' label={when ? `online · ${when}` : 'online'} />;
}

const BoxCard: React.FC<Props> = ({ box, status }) => {
    const containerNames = box.services.filter(s => s.kind === 'container').map(s => s.name);
    // Drift is a running container we never declared — of ANY kind. Compare
    // against every declared service name, not just kind:'container', so a
    // declared timer or host that happens to be running (e.g. certbot after
    // `up -d`, or transiently mid-renew) is not falsely flagged as "Unlisted
    // running" (HOME-251). A genuinely undeclared container still surfaces.
    const extras = extraContainers(status, box.services.map(s => s.name));
    const [selected, setSelected] = useState<Service | undefined>(undefined);

    // Up-count only meaningful when we can actually see the box.
    const upCount = status?.reachable
        ? containerNames.filter(n => resolveContainerState(status, n) === 'up').length
        : undefined;

    return (
        <Paper
            data-testid={`box-${box.id}`}
            sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}
        >
            <Stack direction='row' justifyContent='space-between' alignItems='flex-start' sx={{ mb: 0.5 }}>
                <Box>
                    <Typography variant='h6'>{box.name}</Typography>
                    <Typography variant='caption' color='text.secondary'>{box.role}</Typography>
                </Box>
                {reachabilityChip(status)}
            </Stack>

            {upCount !== undefined && (
                <Typography variant='caption' color='text.secondary' sx={{ mb: 1 }}>
                    {upCount}/{containerNames.length} containers up
                </Typography>
            )}

            <Typography variant='overline' color='text.secondary'>Responsibilities</Typography>
            <Box component='ul' sx={{ mt: 0.5, mb: 1.5, pl: 2.5 }}>
                {box.responsibilities.map(r => (
                    <Typography key={r} component='li' variant='body2' sx={{ mb: 0.25 }}>{r}</Typography>
                ))}
            </Box>

            <Divider sx={{ mb: 1.5 }} />

            <Typography variant='overline' color='text.secondary'>Services</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 0.5 }}>
                {box.services.map(s => (
                    <ServiceChip
                        key={s.name}
                        service={s}
                        state={resolveContainerState(status, s.name)}
                        detail={status?.containers[s.name]?.status}
                        onClick={() => setSelected(s)}
                    />
                ))}
            </Box>

            {selected && (
                <ServiceDetailDialog
                    box={box}
                    service={selected}
                    state={resolveContainerState(status, selected.name)}
                    detail={status?.containers[selected.name]?.status}
                    open={!!selected}
                    onClose={() => setSelected(undefined)}
                />
            )}

            {extras.length > 0 && (
                <Typography variant='caption' sx={{ color: '#ffb74d', mt: 1.5 }}>
                    Unlisted running: {extras.join(', ')}
                </Typography>
            )}

            <Box sx={{ flexGrow: 1 }} />
        </Paper>
    );
};

export default BoxCard;
